import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle, Calendar, MapPin, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_8c6a1162-6fec-4154-8fe3-0b918a141fc6/artifacts/ojlspj1s_Untitled%20design.png';

const RATING_OPTIONS = [
  { value: 1, label: 'Poor', labelAr: 'ضعيف' },
  { value: 2, label: 'Below Avg', labelAr: 'أقل من المتوسط' },
  { value: 3, label: 'Avg', labelAr: 'متوسط' },
  { value: 4, label: 'Good', labelAr: 'جيد' },
  { value: 5, label: 'Great', labelAr: 'ممتاز' },
];

const COURSE_CRITERIA = [
  { key: 'objectives_met', label: 'To what extent did you meet your training objectives?', labelAr: 'إلى أي مدى حققت أهدافك التدريبية؟' },
  { key: 'exercises_useful', label: 'The exercises and examples were useful?', labelAr: 'هل كانت التمارين والأمثلة مفيدة؟' },
  { key: 'material_sequence', label: 'The sequence of the material was smooth?', labelAr: 'هل كان تسلسل المادة سلساً؟' },
  { key: 'overall_quality', label: 'The quality of the training overall?', labelAr: 'جودة التدريب بشكل عام؟' },
  { key: 'customer_support', label: 'The quality of customer support before and during training?', labelAr: 'جودة الدعم قبل وأثناء التدريب؟' },
];

const TRAINER_CRITERIA = [
  { key: 'trainer_knowledge', label: 'Knowledge/experience of the trainer about the topic?', labelAr: 'معرفة/خبرة المدرب بالموضوع؟' },
  { key: 'trainer_delivery', label: 'Did the trainer deliver the material smoothly?', labelAr: 'هل قدم المدرب المادة بسلاسة؟' },
  { key: 'course_organization', label: 'The organization of the course topics?', labelAr: 'تنظيم مواضيع الدورة؟' },
  { key: 'learning_activities', label: 'The use of learning activities and workshops?', labelAr: 'استخدام الأنشطة التعليمية وورش العمل؟' },
];

export default function CourseEvaluationPage() {
  const { enrollmentId } = useParams();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [error, setError] = useState(null);
  
  const [form, setForm] = useState({
    // Course evaluation
    objectives_met: 4,
    exercises_useful: 4,
    material_sequence: 4,
    overall_quality: 4,
    customer_support: 4,
    // Trainer evaluation
    trainer_knowledge: 4,
    trainer_delivery: 4,
    course_organization: 4,
    learning_activities: 4,
    // Open-ended
    what_liked_most: '',
    recommendations: '',
    would_recommend: true,
    general_comments: ''
  });

  useEffect(() => {
    fetchEnrollment();
  }, [enrollmentId]);

  const fetchEnrollment = async () => {
    try {
      const response = await fetch(`${API}/course-evaluation/${enrollmentId}`);
      if (response.ok) {
        const data = await response.json();
        setEnrollment(data);
        if (data.trainee_eval_completed) {
          setSubmitted(true);
        }
      } else {
        setError('Enrollment not found or link is invalid.');
      }
    } catch (err) {
      setError('Failed to load enrollment details.');
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (criterion, value) => {
    setForm(prev => ({ ...prev, [criterion]: parseInt(value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = {
        enrollment_id: enrollmentId,
        ...form
      };
      
      const response = await fetch(`${API}/course-evaluation/${enrollmentId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success(language === 'ar' ? 'تم إرسال التقييم بنجاح' : 'Evaluation submitted successfully');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit evaluation');
      }
    } catch (err) {
      toast.error('Failed to submit evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-xl font-semibold text-[#111827] mb-2">Link Invalid</h2>
            <p className="text-[#6B7280] mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-[#111827] mb-2">
              {language === 'ar' ? 'شكراً لك!' : 'Thank You!'}
            </h2>
            <p className="text-[#6B7280] mb-4">
              {language === 'ar' 
                ? 'تم استلام تقييمك للدورة. ملاحظاتك تساعدنا على التحسين المستمر.'
                : 'Your course evaluation has been received. Your feedback helps us improve continuously.'}
            </p>
            <div className="bg-[#F0F9FF] rounded-lg p-4 text-left">
              <p className="font-medium text-[#111827]">{enrollment.course_title}</p>
              <p className="text-sm text-[#6B7280]">
                {enrollment.city} | {enrollment.start_date} - {enrollment.end_date}
              </p>
            </div>
            <Link to="/" className="inline-block mt-6">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_URL} alt="WeDo HR" className="h-10" />
          </Link>
          <span className="text-sm text-[#6B7280]">Course Evaluation Form</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Course Context Card */}
        <Card className="mb-6 bg-gradient-to-r from-[#33AADD] to-[#2899cc] text-white">
          <CardContent className="p-6">
            <h1 className="text-2xl font-bold mb-4">
              {language === 'ar' ? enrollment.course_title_ar || enrollment.course_title : enrollment.course_title}
            </h1>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-white/90">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span>{language === 'ar' ? enrollment.city_ar || enrollment.city : enrollment.city}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{enrollment.start_date} - {enrollment.end_date}</span>
              </div>
              <div className="flex items-center gap-2 capitalize">
                {enrollment.delivery_mode}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Evaluation Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Course Evaluation Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#33AADD]">
                {language === 'ar' ? 'تقييم الدورة' : 'Course Evaluation'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse" data-testid="course-evaluation-table">
                  <thead>
                    <tr className="bg-[#F9FAFB]">
                      <th className="text-left p-3 border font-medium text-[#374151]">
                        {language === 'ar' ? 'السؤال' : 'Question'}
                      </th>
                      {RATING_OPTIONS.map(opt => (
                        <th key={opt.value} className="text-center p-2 border font-medium text-[#374151] text-xs w-16">
                          {language === 'ar' ? opt.labelAr : opt.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COURSE_CRITERIA.map((criterion, idx) => (
                      <tr key={criterion.key} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'}>
                        <td className="p-3 border text-sm text-[#374151]">
                          {language === 'ar' ? criterion.labelAr : criterion.label}
                        </td>
                        {RATING_OPTIONS.map(opt => (
                          <td key={opt.value} className="text-center p-2 border">
                            <input
                              type="radio"
                              name={criterion.key}
                              value={opt.value}
                              checked={form[criterion.key] === opt.value}
                              onChange={() => handleRatingChange(criterion.key, opt.value)}
                              className="w-4 h-4 text-[#33AADD] focus:ring-[#33AADD]"
                              data-testid={`course-rating-${criterion.key}-${opt.value}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Trainer Evaluation Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#059669]">
                {language === 'ar' ? 'تقييم المدرب' : 'Trainer Evaluation'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse" data-testid="trainer-evaluation-table">
                  <thead>
                    <tr className="bg-[#F9FAFB]">
                      <th className="text-left p-3 border font-medium text-[#374151]">
                        {language === 'ar' ? 'السؤال' : 'Question'}
                      </th>
                      {RATING_OPTIONS.map(opt => (
                        <th key={opt.value} className="text-center p-2 border font-medium text-[#374151] text-xs w-16">
                          {language === 'ar' ? opt.labelAr : opt.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TRAINER_CRITERIA.map((criterion, idx) => (
                      <tr key={criterion.key} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'}>
                        <td className="p-3 border text-sm text-[#374151]">
                          {language === 'ar' ? criterion.labelAr : criterion.label}
                        </td>
                        {RATING_OPTIONS.map(opt => (
                          <td key={opt.value} className="text-center p-2 border">
                            <input
                              type="radio"
                              name={criterion.key}
                              value={opt.value}
                              checked={form[criterion.key] === opt.value}
                              onChange={() => handleRatingChange(criterion.key, opt.value)}
                              className="w-4 h-4 text-[#059669] focus:ring-[#059669]"
                              data-testid={`trainer-rating-${criterion.key}-${opt.value}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Open-ended Feedback Section */}
          <Card>
            <CardHeader>
              <CardTitle>
                {language === 'ar' ? 'ملاحظاتك' : 'Your Feedback'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* What they liked most */}
              <div className="space-y-2">
                <Label htmlFor="what_liked_most" className="text-base font-medium">
                  {language === 'ar' ? 'ما الذي أعجبك أكثر؟' : 'What did you like the most?'}
                </Label>
                <Textarea
                  id="what_liked_most"
                  value={form.what_liked_most}
                  onChange={(e) => setForm(prev => ({ ...prev, what_liked_most: e.target.value }))}
                  rows={3}
                  placeholder={language === 'ar' ? 'أخبرنا عن أفضل جوانب الدورة...' : 'Tell us about the best aspects of the course...'}
                  data-testid="what-liked-most"
                />
              </div>

              {/* Recommendations */}
              <div className="space-y-2">
                <Label htmlFor="recommendations" className="text-base font-medium">
                  {language === 'ar' ? 'ما توصياتك لتحسين هذه الدورة؟' : 'What do you recommend to improve this course?'}
                </Label>
                <Textarea
                  id="recommendations"
                  value={form.recommendations}
                  onChange={(e) => setForm(prev => ({ ...prev, recommendations: e.target.value }))}
                  rows={3}
                  placeholder={language === 'ar' ? 'اقتراحاتك للتحسين...' : 'Your suggestions for improvement...'}
                  data-testid="recommendations"
                />
              </div>

              {/* Would Recommend */}
              <div className="flex items-center space-x-3 p-4 bg-[#F0F9FF] rounded-lg">
                <Checkbox
                  id="would_recommend"
                  checked={form.would_recommend}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, would_recommend: !!checked }))}
                  data-testid="would-recommend"
                />
                <Label htmlFor="would_recommend" className="font-medium cursor-pointer">
                  {language === 'ar' 
                    ? 'هل توصي بـ WeDo Training لزميلك؟' 
                    : 'Would you recommend WeDo Training to your colleague?'}
                </Label>
              </div>

              {/* General Comments */}
              <div className="space-y-2">
                <Label htmlFor="general_comments" className="text-base font-medium">
                  {language === 'ar' ? 'أي تعليقات أو توصيات إضافية' : 'Any comments or recommendations'}
                </Label>
                <Textarea
                  id="general_comments"
                  value={form.general_comments}
                  onChange={(e) => setForm(prev => ({ ...prev, general_comments: e.target.value }))}
                  rows={3}
                  placeholder={language === 'ar' ? 'اختياري...' : 'Optional...'}
                  data-testid="general-comments"
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end">
            <Button 
              type="submit" 
              className="w-full sm:w-auto btn-primary" 
              disabled={submitting}
              data-testid="course-eval-submit"
            >
              {submitting 
                ? (language === 'ar' ? 'جاري الإرسال...' : 'Submitting...') 
                : (language === 'ar' ? 'إرسال التقييم' : 'Submit Evaluation')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
